import { useState, useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Select, Dropdown, Menu } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { PlusIcon, PencilAltIcon, UploadIcon, DocumentTextIcon } from '@heroicons/react/outline'

import { Loader } from '../../../../components'
import { fetchTarifZonesStatus, fetchLastmileXdocks } from '../../../../services'
import { currencyFor, isSpecialZone, weightSortKey, zoneNeedsAction } from './costHelpers'

const T_PREFIX = 'lastmile_management.shipping_costs.status'
const A_PREFIX = 'lastmile_management.shipping_costs.actions'
const PAGE_SIZE = 50

const NotConfigured = ({ label }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
    <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
    {label}
  </span>
)

const NoCoverage = ({ label, title }) => (
  <span
    title={title}
    className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
    {label}
  </span>
)

/**
 * Per-zone table: cost count and cross-dock per zone. A zone with weight tiers can
 * be expanded inline (+/−) to show its "rango de peso / costo" rows. The toolbar
 * hosts Crear / Actualizar costos dropdowns; each row has an "Editar" link.
 */
export const TarifZonesStatusTab = ({
  shippingMethodId,
  country,
  canEdit,
  missingFilter,
  onCreateSingle,
  onCreateBulk,
  onUpdateSingle,
  onUpdateBulk,
  onEditCostsRow,
  t,
}) => {
  const [selectedZones, setSelectedZones] = useState([])
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState({ key: 'zone', dir: 'asc' })
  const [expanded, setExpanded] = useState(() => new Set())

  const toggleSort = key => setSort(prev => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  const toggleExpand = zone =>
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(zone) ? next.delete(zone) : next.add(zone)
      return next
    })

  const { data, isLoading, isFetching } = useQuery(['tarif_zones_status', shippingMethodId, '', false], () => fetchTarifZonesStatus(shippingMethodId), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  const methodIsCubbo = !!data?.is_cubbo

  // Resolve zone -> cross-dock name for the "Zona xdock" column.
  const { data: xdocks = [] } = useQuery(['lastmile-xdocks', country], () => fetchLastmileXdocks({ country, includeZones: true }), {
    enabled: methodIsCubbo && Boolean(country),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
  const xdockByZone = useMemo(() => {
    const map = {}
    xdocks.forEach(x => (x.zone_names || []).forEach(zn => (map[zn] = x.name)))
    return map
  }, [xdocks])

  const catalogZones = useMemo(() => data?.zones || [], [data])
  const zoneOptions = useMemo(() => catalogZones.map(z => ({ label: z.zone, value: z.zone })), [catalogZones])

  const filtered = useMemo(() => {
    let list = selectedZones.length === 0 ? catalogZones : catalogZones.filter(z => selectedZones.includes(z.zone))
    // "Ver zonas" from the action banner: zones with a runtime-breaking gap (backend `missing`).
    if (missingFilter === 'action') {
      list = list.filter(zoneNeedsAction)
    }
    return list
  }, [catalogZones, selectedZones, missingFilter])

  const zones = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => String(a.zone).localeCompare(String(b.zone), undefined, { numeric: true }) * dir)
  }, [filtered, sort])

  useEffect(() => {
    setPage(1)
  }, [selectedZones, missingFilter])

  const totalPages = Math.max(1, Math.ceil(zones.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageStart = (currentPage - 1) * PAGE_SIZE
  const pagedZones = zones.slice(pageStart, pageStart + PAGE_SIZE)
  const currency = currencyFor(country)

  const createMenu = (
    <Menu onClick={({ key }) => (key === 'single' ? onCreateSingle?.() : onCreateBulk?.())}>
      <Menu.Item key="single" icon={<PlusIcon className="h-4 w-4" />}>
        {t(`${A_PREFIX}.create_single`)}
      </Menu.Item>
      <Menu.Item key="bulk" icon={<UploadIcon className="h-4 w-4" />}>
        {t(`${A_PREFIX}.bulk`)}
      </Menu.Item>
    </Menu>
  )
  const updateMenu = (
    <Menu onClick={({ key }) => (key === 'single' ? onUpdateSingle?.() : onUpdateBulk?.())}>
      <Menu.Item key="single" icon={<PencilAltIcon className="h-4 w-4" />}>
        {t(`${A_PREFIX}.update_single`)}
      </Menu.Item>
      <Menu.Item key="bulk" icon={<DocumentTextIcon className="h-4 w-4" />}>
        {t(`${A_PREFIX}.bulk`)}
      </Menu.Item>
    </Menu>
  )

  // Weight-tier rows for a zone's expanded panel: turn the sorted ceilings into
  // ranges ("≤ 5 kg", "5 – 10 kg", …) paired with the formatted cost.
  const tierRows = z => {
    const tiers = [...(z.tiers || [])].sort((a, b) => weightSortKey(a.max_compound_weight) - weightSortKey(b.max_compound_weight))
    return tiers.map((tier, i) => {
      const amount = Number(tier.cost)
      return {
        id: tier.shipping_cost_id ?? i,
        // Surface the shipping_cost_id: it's the key the "Actualizar costos en bloque" CSV
        // needs, and this expander is the only place the operator can read it.
        costId: tier.shipping_cost_id ?? null,
        weight: (() => {
          const max = tier.max_compound_weight
          const min = i === 0 ? 0 : tiers[i - 1].max_compound_weight
          // A tier with no ceiling is the open-ended top band: "Más de {min} kg".
          if (max === null || max === undefined || max === '') return t(`${T_PREFIX}.tier_range_open`, { min })
          return i === 0 ? t(`${T_PREFIX}.tier_range_first`, { max }) : t(`${T_PREFIX}.tier_range`, { min, max })
        })(),
        cost: t(`${T_PREFIX}.tier_cost`, { currency, cost: Number.isFinite(amount) ? amount.toFixed(2) : tier.cost }),
        selectable: tier.selectable,
      }
    })
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Toolbar: zone filter (left) + cost actions (right) */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
        <Select
          mode="multiple"
          allowClear
          showSearch
          value={selectedZones}
          onChange={setSelectedZones}
          options={zoneOptions}
          placeholder={t(`${T_PREFIX}.filter_placeholder`)}
          maxTagCount="responsive"
          className="min-w-[16rem] flex-1 max-w-md"
          optionFilterProp="label"
        />

        {canEdit && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Dropdown trigger={['click']} overlay={updateMenu}>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <PencilAltIcon className="h-4 w-4" />
                {t(`${A_PREFIX}.update_costs`)}
                <DownOutlined className="text-xs" />
              </button>
            </Dropdown>
            <Dropdown trigger={['click']} overlay={createMenu}>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <PlusIcon className="h-4 w-4" />
                {t(`${A_PREFIX}.create_costs`)}
                <DownOutlined className="text-xs" />
              </button>
            </Dropdown>
          </div>
        )}
      </div>

      {isLoading ? (
        <Loader show={true} />
      ) : zones.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">{t(`${T_PREFIX}.empty`)}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y divide-gray-200 text-sm ${isFetching ? 'opacity-60' : ''}`}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                  <button type="button" onClick={() => toggleSort('zone')} className="inline-flex items-center gap-1 uppercase hover:text-gray-700">
                    {t(`${T_PREFIX}.col_zone`)}
                    <span className="text-gray-400">{sort.key === 'zone' ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                  </button>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_cost`)}</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_xdock`)}</th>
                {canEdit && <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_edit`)}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {pagedZones.map(z => {
                const allTiers = z.tiers || []
                const tierCount = allTiers.length
                const activeCount = allTiers.filter(ti => ti.selectable).length
                const isOpen = expanded.has(z.zone)
                // A zone no coverage declares is priced-but-unreachable: no zipcode resolves to it, so its
                // tiers never price an order, and `sync_zone` refuses to save them (ZONE_NOT_COVERED). Say so
                // on the row and keep the editor shut instead of letting the operator find out after typing.
                // Strict `=== false` so a response without the field never disables the action, and the
                // special zones the backend exempts from the guard stay editable.
                const uncovered = z.has_coverage === false && !isSpecialZone(z.zone)
                return [
                  <tr
                    key={z.zone}
                    onClick={tierCount > 0 ? () => toggleExpand(z.zone) : undefined}
                    className={`hover:bg-gray-50 ${tierCount > 0 ? 'cursor-pointer select-none' : ''}`}
                  >
                    {/* Zona + expander */}
                    <td className="px-4 py-2 font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {tierCount > 0 ? (
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation()
                              toggleExpand(z.zone)
                            }}
                            className="inline-flex h-5 w-5 items-center justify-center rounded border border-gray-300 text-gray-500 hover:bg-gray-100"
                            aria-label={z.zone}
                          >
                            {isOpen ? '−' : '+'}
                          </button>
                        ) : (
                          <span className="inline-block h-5 w-5" />
                        )}
                        {z.zone}
                        {uncovered && <NoCoverage label={t(`${T_PREFIX}.no_coverage`)} title={t(`${T_PREFIX}.no_coverage_hint`)} />}
                      </div>
                    </td>
                    {/* Costos — count only active (runtime-selectable) tiers */}
                    <td className="px-4 py-2">
                      {activeCount > 0 ? (
                        <span className="text-gray-700">{t(`${T_PREFIX}.costs_count`, { count: activeCount })}</span>
                      ) : (
                        <NotConfigured label={t(`${T_PREFIX}.not_configured`)} />
                      )}
                    </td>
                    {/* Zona xdock */}
                    <td className="px-4 py-2">
                      {!z.is_cubbo ? (
                        <span className="text-gray-400">{t(`${T_PREFIX}.not_applicable`)}</span>
                      ) : z.has_xdock_zone ? (
                        <span className="text-gray-700">{xdockByZone[z.zone] || '—'}</span>
                      ) : (
                        <NotConfigured label={t(`${T_PREFIX}.not_configured`)} />
                      )}
                    </td>
                    {/* Editar */}
                    {canEdit && (
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          disabled={uncovered}
                          title={uncovered ? t(`${T_PREFIX}.no_coverage_hint`) : undefined}
                          onClick={e => {
                            e.stopPropagation()
                            onEditCostsRow?.(z)
                          }}
                          className={`text-sm font-medium ${uncovered ? 'cursor-not-allowed text-gray-400' : 'text-indigo-600 hover:text-indigo-900'}`}
                        >
                          {t(`${T_PREFIX}.edit`)}
                        </button>
                      </td>
                    )}
                  </tr>,
                  ...(isOpen && tierCount > 0
                    ? [
                        <tr key={`${z.zone}-th`} className="bg-gray-50">
                          <td className="py-1 pl-11 pr-4 text-xs font-medium uppercase text-gray-400">{t(`${T_PREFIX}.col_tier_weight`)}</td>
                          <td className="px-4 py-1 text-xs font-medium uppercase text-gray-400">{t(`${T_PREFIX}.col_tier_cost`)}</td>
                          <td className="px-4 py-1 text-xs font-medium uppercase text-gray-400">{t(`${T_PREFIX}.col_tier_id`)}</td>
                          {canEdit && <td />}
                        </tr>,
                        ...tierRows(z).map(row => (
                          <tr key={`${z.zone}-${row.id}`} className="bg-gray-50">
                            <td className={`py-0.5 pl-11 pr-4 text-sm tabular-nums ${row.selectable ? 'text-gray-600' : 'text-gray-400 line-through'}`}>
                              {row.weight}
                            </td>
                            <td className={`px-4 py-0.5 text-sm tabular-nums ${row.selectable ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                              {row.cost}
                            </td>
                            {/* shipping_cost_id — the key the bulk-update CSV needs (J18). */}
                            <td className="px-4 py-0.5 font-mono text-xs text-gray-400 tabular-nums">{row.costId != null ? row.costId : '—'}</td>
                            {canEdit && <td />}
                          </tr>
                        )),
                      ]
                    : []),
                ]
              })}
            </tbody>
          </table>

          {zones.length > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
              <span>
                {t(`${T_PREFIX}.showing`, {
                  from: pageStart + 1,
                  to: pageStart + pagedZones.length,
                  total: zones.length,
                })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t(`${T_PREFIX}.prev`)}
                </button>
                <span className="tabular-nums">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t(`${T_PREFIX}.next`)}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
